import { useCallback, useEffect, useState } from "react";
import { LogOut, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { Materials } from "@/components/account/Materials";
import { Contracts } from "@/components/account/Contracts";

const Shell = ({ children }) => <main data-testid="account-page" className="min-h-screen bg-white px-5 pb-24 pt-32 text-[#1c1418] md:px-10"><div className="mx-auto max-w-4xl space-y-8">{children}</div></main>;

const Cancelled = ({ data }) => (
  <section data-testid="account-cancelled" className="space-y-5 bg-[#f4f4f4] p-6 md:p-10">
    <h2 className="font-display text-4xl uppercase leading-none">Richiesta scaduta</h2>
    <p>Sono passati 15 giorni senza firma, quindi il form e la richiesta sono stati cancellati. Puoi riattivare il processo di firma richiedendolo su WhatsApp al <b>{data.whatsapp}</b>.</p>
    <a href={data.whatsapp_link} target="_blank" rel="noopener noreferrer" data-testid="account-whatsapp" className="inline-flex items-center gap-2 bg-[#1c1418] px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white">
      <MessageCircle className="h-4 w-4 text-[#e9458f]" /> Scrivici su WhatsApp
    </a>
  </section>
);

const Status = ({ data }) => (
  data.status === "completed"
    ? <p data-testid="account-completed" className="bg-[#1c1418] p-5 text-white">Tutto firmato, grazie! Ti contatteremo a breve per i prossimi passi.</p>
    : <p data-testid="account-days-left" className="bg-[#f4f4f4] p-5 text-sm">Hai ancora <b>{data.days_left} giorni</b> per completare tutto. Dopo 15 giorni senza firma la richiesta viene cancellata.</p>
);

export default function Account() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    api.get("/account").then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "Errore"));
  }, []);
  useEffect(() => { if (user?.role === "model") load(); }, [user, load]);

  if (user === null) return <main className="min-h-screen bg-white" />;
  if (!user) return <AdminLogin title="La tua area" />;
  if (user.role !== "model") return <Shell><p className="font-display text-4xl uppercase">Questa è l'area modelle. Lo staff usa /admin o /scout.</p></Shell>;
  if (error) return <Shell><p data-testid="account-error" className="font-display text-4xl uppercase">{error}</p></Shell>;
  if (!data) return <Shell />;

  return (
    <Shell>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/70">La tua area · Youth Management</p>
          <h1 data-testid="account-title" className="font-display text-5xl uppercase leading-[0.9] md:text-7xl">Ciao {data.first_name}</h1>
        </div>
        <button onClick={logout} data-testid="account-logout" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]"><LogOut className="h-4 w-4" /> Esci</button>
      </div>
      {data.status === "cancelled" ? <Cancelled data={data} /> : (
        <>
          <Status data={data} />
          <Materials data={data} reload={load} />
          <Contracts data={data} />
        </>
      )}
    </Shell>
  );
}
